import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import useSWR from "swr";

import ProductDetails from "./components/product-details";
import RelatedProducts from "./components/related-products";
import TrendingProducts from "../new_home_page/trending_products/product-slider";

import {
  addToCart,
  addToCartUnsafe,
  addToWishlist,
  removeFromWishlist,
  addToCompare,
  removeFromCompare,
  addCartToServer,
} from "../../actions";
import { getPdpCollection } from "../../services/index";

import "./css/main.css";

import Container from "../system/Container";
import SunnyPromise from "./components/sunnyPromise";
import ProductHighlights from "./components/product-highlights";
import PdpShimmer from "./components/PdpShimmer";
// import algoliasearch from 'algoliasearch/lite';

const removeNonPricedProduct = (product) => {
  const refinedProduct = { ...product };
  refinedProduct.variants = product.variants.filter(
    (variant) => variant.offerprice > 0,
  );
  return refinedProduct;
};

const fetchPdp = async (slug) => {
  const response = await getPdpCollection({ value: slug });
  if (
    response.data &&
    response.data.success === true &&
    response.data.result.variants.length > 0
  ) {
    const refinedProduct = removeNonPricedProduct(response.data.result);
    if (refinedProduct.variants.length > 0) {
      return refinedProduct;
    }
  }
  return null;
};

const PdpDetails = (props) => {
  const slug = props.match.params.id;
  const { data: allProducts, error: pdpError } = useSWR(
    slug ? `pdp-${slug}` : null,
    () => fetchPdp(slug),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: Infinity,
    },
  );

  const [state, setState] = useState({
    variants: [],
    images: [],
  });
  const [searchResults, setSearchResults] = useState([]);

  function urlParamter(product) {
    const queryParams = new URLSearchParams(window.location.search);
    const variant_id = queryParams.get("variant_id");
    let selectedVariant = [];
    let list = product.variants.map((item) => item.id == variant_id);
    if (list.includes(true))
      selectedVariant = product.variants.filter(
        (item) => item.id == variant_id,
      )[0];
    else {
      window.history.replaceState({}, "", window.location.pathname);
      selectedVariant = product.variants[0];
    }
    if (!selectedVariant) return;
    setState({
      variants: selectedVariant,
      images: selectedVariant.image ? selectedVariant.image.split(",") : [],
    });
  }

  useEffect(() => {
    document.body.classList.add("product-detail");
    return () => {
      document.body.classList.remove("product-detail");
    };
  }, []);

  // Push analytics when product data loads
  useEffect(() => {
    if (allProducts && allProducts.variants) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "view_item",
          ecommerce: {
            detail: {
              actionField: { list: "pdp" },
              products: [
                {
                  name: allProducts.name,
                  id: allProducts.sku,
                  price: allProducts.variants[0].offerprice,
                  brand: "Sunny Diamonds",
                  category: allProducts.category,
                  variant: allProducts.variants[0].color,
                },
              ],
            },
          },
        });
      } catch (err) {
        window.dataLayer = window.dataLayer || [];
        if (
          window.dataLayer.filter(function(obj) {
            return obj.errorMsg === err.message;
          }).length == 0
        ) {
          window.dataLayer.push({
            event: "variable error",
            errorMsg: err.message,
          });
        }
      }

      if (Array.isArray(window.trending_products))
        setSearchResults(window.trending_products.slice(5, 15));
      urlParamter(allProducts);
    }
  }, [allProducts]);

  // Redirect to 404 if fetch resolved but returned null (product not found)
  useEffect(() => {
    if (allProducts === null || pdpError) {
      props.history.replace("/404");
    }
  }, [allProducts, pdpError]);

  // console.log(
  //   props.allProducts &&
  //     props.allProducts.variants[0] &&
  //     props.allProducts.variants[0].rating,
  //   "props"
  // );

  // Add LocalBusiness schema to document head
  useEffect(() => {
    const schemaData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name:
        props.allProducts && props.allProducts.name && props.allProducts.name,
      image:
        props.allProducts &&
        props.allProducts.variants &&
        props.allProducts.variants[0] &&
        props.allProducts.variants[0].image &&
        props.allProducts.variants[0].image.split(",")[0],
      description:
        props.allProducts &&
        props.allProducts.description &&
        props.allProducts.description,
      brand: {
        "@type": "Brand",
        name: "Sunny Diamonds",
      },
      sku: props.allProducts && props.allProducts.sku,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue:
          props.allProducts &&
          props.allProducts.variants &&
          props.allProducts.variants[0] &&
          props.allProducts.variants[0].rating,
        // bestRating: "5",
        // worstRating: "1",
        // ratingCount: "4",
      },
    };

    // Create script element
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-schema";
    script.textContent = JSON.stringify(schemaData);

    // Add to head
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      const existingScript = document.getElementById("product-schema");
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  const setVariant = (vari) => {
    if (!vari) return;
    setState((prev) => ({
      variants: vari,
      images: vari.image ? vari.image.split(",") : prev.images,
    }));
  };

  const {
    symbol,
    addToCart,
    addToCartUnsafe,
    addToWishlist,
    removeFromWishlist,
    addToCompare,
    removeFromCompare,
    addCartToServer,
    cartItems,
    wishListItems,
    compareItems,
  } = props;

  const { variants, images } = state;

  if (allProducts && allProducts.variants)
    return (
      <div className="site-main">
        <Helmet>
          <title>Buy {allProducts.name} | Sunny Diamonds</title>
          <meta
            name="description"
            content={`Buy ${allProducts.name} from Sunny Diamonds. Effortlessly elegant, made to complement every moment. Explore now.`}
          />
          <meta name="keywords" content={allProducts.name} />
          <meta property="og:site_name" content="Sunny Diamonds" />
          <meta property="og:type" content="Summary" />
          <meta property="og:title" content={allProducts.name} />
          <meta
            property="og:description"
            content={`Buy ${allProducts.name} from Sunny Diamonds. Effortlessly elegant, made to complement every moment. Explore now.`}
          />
          <meta property="og:url" content={window.location.href} />
          <meta
            property="og:image"
            content={"https://sunnydiamonds.com" + images[0]}
          />
          <meta name="twitter:card" content="Summary" />
          <meta name="twitter:site" content="@SunnyDiamond_4c" />
          <meta name="twitter:creator" content="Sunny Diamonds" />
          <meta name="twitter:title" content={allProducts.name} />
          <meta
            name="twitter:image"
            content={"https://sunnydiamonds.com" + images[0]}
          />
          <meta
            name="twitter:description"
            content={variants.short_description}
          />
          <meta name="robots" content="all" />
          <link rel="canonical" href={window.location.href} />
        </Helmet>

        <ProductDetails
          symbol={symbol}
          item={allProducts}
          cartItems={cartItems}
          addToCartClicked={addToCart}
          addCartToServer={addCartToServer}
          BuynowClicked={addToCartUnsafe}
          addToWishlistClicked={addToWishlist}
          onRemoveFromWishlistClicked={removeFromWishlist}
          addToCompareClicked={addToCompare}
          removeFromCompareClicked={removeFromCompare}
          wishListedItem={wishListItems}
          compareItems={compareItems}
          setVariant={setVariant}
          history={props.history}
          itemSelected={variants}
        />


        {/* <RelatedProducts sku={allProducts.sku} /> */}

        {/* <TrendingProducts searchResults={searchResults} /> */}

        <ProductHighlights title="You may Also Like" searchResults={searchResults} />
        <SunnyPromise innerPage />


        {/* Section Commented - Not in new design */}
        {/* <Container>
          <InstagramWidget />
        </Container> */}
      </div>
    );
  else
    return (
      <main className="site-main">
        <PdpShimmer symbol={symbol} />
      </main>
    );
};

const mapStateToProps = (state) => {
  return {
    symbol: state.data.symbol,
    cartItems: state.cartList.cart,
    wishListItems: state.wishlist.list,
    compareItems: state.compare.items,
  };
};

export default connect(mapStateToProps, {
  addToCart,
  addToCartUnsafe,
  addCartToServer,
  addToWishlist,
  removeFromWishlist,
  addToCompare,
  removeFromCompare,
})(PdpDetails);
